<template>
  <div
    class="wc-menu-dropdown-modern"
    :class="variant ? `wc-menu-dropdown-modern--${variant}` : null"
    ref="root"
    :style="panelPositionVars"
  >
    <button
      type="button"
      class="wc-menu-dropdown-modern__trigger"
      @click.stop="toggleDropdown"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-controls="isOpen ? panelId : null"
      :aria-label="ariaLabel"
    >
      <slot name="trigger" :label="label" :is-open="isOpen">
        <span class="wc-menu-dropdown-modern__value">{{ label }}</span>
        <WCSvgIcon name="chevron-down" class="wc-menu-dropdown-modern__chevron" icon-size="sm" />
      </slot>
    </button>

    <div
      v-if="isOpen"
      ref="panel"
      :id="panelId"
      class="wc-menu-dropdown-modern__panel"
      :class="{
        'wc-menu-dropdown-modern__panel--up': dropUp,
        'wc-menu-dropdown-modern__panel--fixed': overlayMenu,
      }"
    >
      <ul class="wc-menu-dropdown-modern__list" :aria-label="ariaLabel">
        <li v-for="(option, index) in options" :key="index" class="wc-menu-dropdown-modern__item">
          <button
            type="button"
            class="wc-menu-dropdown-modern__action"
            :aria-selected="option.selected ? 'true' : null"
            @click.stop="selectOption(option)"
          >
            <slot name="option" :option="option" :selected="!!option.selected">
              <span class="wc-menu-dropdown-modern__check" aria-hidden="true">
                <WCSvgIcon
                  v-if="option.selected"
                  name="tick-icon"
                  class="wc-menu-dropdown-modern__tick"
                />
              </span>
              <span class="wc-menu-dropdown-modern__label">{{ option.label }}</span>
            </slot>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted, onBeforeUnmount, defineProps, defineEmits, withDefaults } from 'vue';
import { v4 as uuid } from 'uuid';
import WCSvgIcon from '@/components/WCSvgIcon/WCSvgIcon.vue';

interface DropdownOption {
  value: string | number | object;
  label: string;
  selected?: boolean;
}

const props = withDefaults(
  defineProps<{
    options: DropdownOption[];
    label?: string;
    ariaLabel?: string;
    variant?: string;
    overlayMenu?: boolean;
  }>(),
  {
    label: '',
    ariaLabel: '',
    variant: '',
    overlayMenu: false,
  },
);
const emit = defineEmits<{ select: [value: DropdownOption['value']] }>();

const DROPDOWN_OPEN_EVENT = 'wc-dropdown-open';

const panelId = `wc-menu-dropdown-modern-panel-${uuid()}`;
const root = ref<HTMLElement | null>(null);
const panel = ref<HTMLElement | null>(null);
const isOpen = ref(false);
const dropUp = ref(false);
const panelPositionVars = ref<Record<string, string>>({});

const closeDropdown = (): void => {
  isOpen.value = false;
  panelPositionVars.value = {};
};

const updatePanelPosition = (): void => {
  const anchor = props.overlayMenu ? root.value?.parentElement : root.value;
  if (!anchor || !panel.value) return;
  const rect = anchor.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;
  dropUp.value = spaceBelow < panel.value.offsetHeight && spaceAbove > spaceBelow;
  panelPositionVars.value = {
    '--wc-dd-left': `${rect.left}px`,
    '--wc-dd-width': `${rect.width}px`,
    '--wc-dd-top': `${rect.bottom}px`,
    '--wc-dd-bottom': `${viewportHeight - rect.top}px`,
  };
};

const toggleDropdown = async (): Promise<void> => {
  if (isOpen.value) {
    closeDropdown();
    return;
  }
  isOpen.value = true;
  document.dispatchEvent(new CustomEvent(DROPDOWN_OPEN_EVENT, { detail: panelId }));
  await nextTick();
  updatePanelPosition();
};

const selectOption = (option: DropdownOption): void => {
  emit('select', option.value);
  closeDropdown();
};

const onDocumentClick = (event: MouseEvent): void => {
  if (root.value && !root.value.contains(event.target as Node)) {
    closeDropdown();
  }
};

const onScroll = (event: Event): void => {
  if (root.value?.contains(event.target as Node)) return;
  closeDropdown();
};

const onOtherDropdownOpen = (event: Event): void => {
  if ((event as CustomEvent<string>).detail !== panelId) {
    closeDropdown();
  }
};

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
  document.addEventListener(DROPDOWN_OPEN_EVENT, onOtherDropdownOpen);
  window.addEventListener('scroll', onScroll, { capture: true, passive: true });
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener(DROPDOWN_OPEN_EVENT, onOtherDropdownOpen);
  window.removeEventListener('scroll', onScroll, { capture: true });
});
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-menu-dropdown-modern {
  position: relative;
  flex: 1;
  min-width: 0;
}

.wc-menu-dropdown-modern__trigger {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
}

.wc-menu-dropdown-modern__value {
  flex: 1;
  min-width: 0;
  text-align: center;
  font-size: var(--wc-menu-dropdown-trigger-font-size, 1rem);
  font-weight: var(--wc-menu-dropdown-trigger-font-weight, 700);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wc-menu-dropdown-modern__chevron {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  color: inherit;
  :deep(.wc-icon) {
    color: inherit;
  }
}

.wc-menu-dropdown-modern__panel {
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  right: 0;
  z-index: 10000;
  background-color: var(--wc-menu-dropdown-panel-bg, var(--white));
  border: 1px solid var(--wc-menu-dropdown-border-color, var(--dark-lighter-5));
  border-radius: 0.375rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.wc-menu-dropdown-modern__panel--up {
  top: auto;
  bottom: calc(100% + 0.5rem);
}

.wc-menu-dropdown-modern__panel--fixed {
  position: fixed;
  left: var(--wc-dd-left);
  width: var(--wc-dd-width);
  right: auto;
  top: calc(var(--wc-dd-top) + 0.5rem);
  bottom: auto;

  &.wc-menu-dropdown-modern__panel--up {
    top: auto;
    bottom: calc(var(--wc-dd-bottom) + 0.5rem);
  }
}

.wc-menu-dropdown-modern__list {
  max-height: calc((2.25rem * 7) + 0.27rem);
  overflow-y: auto;
  list-style: none;
  margin: 0;
  padding: 0.25rem 0;
}

.wc-menu-dropdown-modern__item {
  display: block;
  position: relative;

  &:not(:first-child)::before {
    content: '';
    position: absolute;
    inset: 0 1rem auto;
    border-top: 1px solid var(--wc-menu-dropdown-border-color, var(--dark-lighter-5));
  }
}

.wc-menu-dropdown-modern__action {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  min-height: 2.25rem;
  padding: 0.5rem 1rem;
  border: 0;
  background: transparent;
  color: var(--wc-menu-dropdown-item-color, var(--body-color, $body-color));
  cursor: pointer;
  text-align: left;
  font-size: var(--wc-menu-dropdown-item-font-size, 0.875rem);
  font-weight: 400;

  &:hover,
  &:focus {
    outline: none;
    background-color: var(
      --wc-menu-dropdown-item-hover-bg,
      var(--primary-lighter-9, var(--primary))
    );
  }
}

.wc-menu-dropdown-modern__check {
  width: 1rem;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--wc-menu-dropdown-check-color, var(--primary));
}

.wc-menu-dropdown-modern__label {
  flex: 1;
  white-space: nowrap;
}
</style>
